import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonIcon,
  IonButton,
  IonButtons,
} from '@ionic/react';
import {
  homeOutline,
  home,
  notificationsOutline,
  notifications,
  briefcaseOutline,
  briefcase,
  personCircleOutline,
  personCircle,
  arrowBackOutline,
  menuOutline,
  closeOutline,
  logOutOutline,
} from 'ionicons/icons';
import {
  Home as HomeIcon,
  Briefcase,
  FileText,
  Users,
  HelpCircle,
  Calendar,
  UserCircle,
  LogOut,
} from 'lucide-react';
import logomark from '../../assets/capitol-commons-logomark.svg';
import { Capacitor } from '@capacitor/core';
import { applyNativeStatusBar } from '@shared/native/statusBar';
import type { LucideIcon } from 'lucide-react';
import api from '../../lib/api';
import { getArticleBySlug } from '../../help/content';
import { useProPushNotifications } from '../../hooks/useProPushNotifications';
import ProPageModal from './bottom-sheet/ProPageModal';
import { proSignOut } from '../../pages/pro/proSignOut';
import {
  useProToolbarEndSlot,
  useProToolbarStickyValue,
} from './ProToolbarSlot';
import { useProShellNavigate } from '../../utils/proShellNavigation';
import { useProScrollReset } from '../../hooks/useProScrollReset';
import { requestProLeave, setProDrawerCloser } from './proBackPolicy';
interface DrawerItem {
  nameKey: string;
  href: string;
  icon: LucideIcon;
  /** When true, the item renders disabled with a "Soon" badge and doesn't navigate. */
  placeholder?: boolean;
}

const DRAWER_ITEMS: DrawerItem[] = [
  { nameKey: 'nav.home', href: '/pro/home', icon: HomeIcon },
  { nameKey: 'nav.workspaces', href: '/pro/workspaces', icon: Briefcase },
  { nameKey: 'nav.bills', href: '/pro/bills', icon: FileText },
  { nameKey: 'nav.calendar', href: '/pro/calendar', icon: Calendar },
  { nameKey: 'nav.legislators', href: '/pro/legislators', icon: Users },
  { nameKey: 'nav.help', href: '/pro/help', icon: HelpCircle },
];

const ProTabLayout: React.FC = () => {
  const { t } = useTranslation('pro');
  const location = useLocation();
  const navigate = useNavigate();
  const shellNavigate = useProShellNavigate();
  const contentRef = useProScrollReset();
  const [unreadCount, setUnreadCount] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const pageSticky = useProToolbarStickyValue();

  // ProRouteGuard ensures we only mount when authenticated.
  useProPushNotifications(true);

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const { data } = await api.get('/notifications/unread-count');
        if (data.success) {
          setUnreadCount(data.data?.unreadCount ?? data.data?.count ?? 0);
        }
      } catch {
        // Notifications endpoint may not exist yet
      }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => clearInterval(interval);
  }, []);

  // Close drawer on navigation
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Status bar: purple backdrop with light icons at rest. When the drawer is
  // open, it covers the top with a white header so we flip to dark icons.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const apply = async () => {
      if (drawerOpen) {
        await applyNativeStatusBar({ iconStyle: 'dark', backgroundColor: '#ffffff' });
      } else {
        await applyNativeStatusBar({ iconStyle: 'light', backgroundColor: '#4D3161' });
      }
    };
    apply();
  }, [drawerOpen]);

  // Hardware back closes the drawer before leave handlers / navigation.
  useEffect(() => {
    setProDrawerCloser(() => {
      if (!drawerOpen) return false;
      setDrawerOpen(false);
      return true;
    });
    return () => setProDrawerCloser(null);
  }, [drawerOpen]);

  const goBack = useCallback(() => {
    requestProLeave(() => navigate(-1));
  }, [navigate]);

  const guardedShellNavigate = useCallback(
    (path: string) => {
      requestProLeave(() => shellNavigate(path));
    },
    [shellNavigate],
  );

  const getActiveTab = (): string => {
    const path = location.pathname;
    if (path.startsWith('/pro/notifications')) return 'notifications';
    if (path.startsWith('/pro/workspaces')) return 'workspaces';
    if (path.startsWith('/pro/profile')) return 'profile';
    return 'home';
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/pro/home') return t('pageTitles.capitolCommons');
    if (path === '/pro/notifications') return t('pageTitles.alerts');
    if (path === '/pro/bills') return t('pageTitles.bills');
    if (path === '/pro/legislators') return t('pageTitles.legislators');
    if (path === '/pro/workspaces') return t('pageTitles.workspaces');
    if (path === '/pro/calendar') return t('pageTitles.calendar');
    if (path === '/pro/profile') return t('pageTitles.profile');
    if (path === '/pro/profile/edit') return 'Edit profile';
    if (path === '/pro/profile/security') return 'Password';
    if (path === '/pro/profile/notifications') return 'Notifications';
    if (path === '/pro/profile/delete') return 'Delete account';
    if (path === '/pro/help') return t('pageTitles.help');
    if (path.startsWith('/pro/help/')) {
      // Show the article's real title in the app bar; fall back to the
      // generic label if the slug doesn't resolve to a known article.
      const slug = path.slice('/pro/help/'.length);
      return getArticleBySlug(slug)?.title ?? t('pageTitles.helpArticle');
    }
    if (path.startsWith('/pro/bills/')) return t('pageTitles.billDetail');
    if (path.startsWith('/pro/legislators/')) return t('pageTitles.legislator');
    if (path.startsWith('/pro/workspaces/')) return t('pageTitles.workspaceDetail');
    return t('pageTitles.capitolCommons');
  };

  const activeTab = getActiveTab();

  const BASE_TAB_PATHS = ['/pro/home', '/pro/notifications', '/pro/bills', '/pro/legislators', '/pro/workspaces', '/pro/calendar', '/pro/profile'];
  const isDetailPage = !BASE_TAB_PATHS.includes(location.pathname);

  /** Screens with toolbar pinned in IonHeader (drawer list tabs + bottom-tab roots). */
  const TAB_ROOT_PATHS = [
    '/pro/home',
    '/pro/workspaces',
    '/pro/notifications',
    '/pro/profile',
    '/pro/bills',
    '/pro/legislators',
    '/pro/calendar',
  ];
  const sticky = pageSticky || TAB_ROOT_PATHS.includes(location.pathname);

  const tabs = [
    { key: 'home', path: '/pro/home', label: t('nav.home'), activeIcon: home, inactiveIcon: homeOutline },
    { key: 'workspaces', path: '/pro/workspaces', label: t('nav.workspaces'), activeIcon: briefcase, inactiveIcon: briefcaseOutline },
    { key: 'notifications', path: '/pro/notifications', label: t('nav.alerts'), activeIcon: notifications, inactiveIcon: notificationsOutline },
    { key: 'profile', path: '/pro/profile', label: t('nav.profile'), activeIcon: personCircle, inactiveIcon: personCircleOutline },
  ];

  const tabBarHeight = 56;

  // Shared toolbar JSX — pinned in IonHeader when `sticky` is true (tab roots
  // or pages that call `useProToolbarSticky(true)`), otherwise scrolls inside
  // IonContent.
  const toolbar = (
    <IonToolbar className="pro-static-toolbar">
      <IonButtons slot="start">
        {isDetailPage ? (
          <IonButton
            fill="clear"
            onClick={goBack}
            className="text-white"
            aria-label={t('a11y.back')}
          >
            <IonIcon slot="icon-only" icon={arrowBackOutline} />
          </IonButton>
        ) : (
          <IonButton
            fill="clear"
            onClick={() => setDrawerOpen(true)}
            className="text-white"
            aria-label={t('a11y.openMenu')}
          >
            <IonIcon slot="icon-only" icon={menuOutline} />
          </IonButton>
        )}
      </IonButtons>
      <IonTitle className="text-base font-bold">
        <span className="block truncate">{getPageTitle()}</span>
      </IonTitle>
      <ToolbarEndSlot />
    </IonToolbar>
  );

  return (
    <div className="pro-ionic citizen-ionic" style={{ height: '100%', width: '100%' }}>
      <div className="citizen-app-shell">
        {/*
         * Status-bar backdrop: fixed purple strip sized to the top safe-area
         * inset. The native status bar icons sit on top of this, so on iOS
         * (where the system status bar is transparent over the webview) we
         * get a branded purple header strip even as the toolbar scrolls away.
         */}
        <div
          aria-hidden
          className="fixed top-0 left-0 right-0 z-[9998]"
          style={{
            height: 'env(safe-area-inset-top, 0px)',
            backgroundColor: drawerOpen ? '#ffffff' : '#4D3161',
          }}
        />
        <IonPage>
          {/*
           * Toolbar placement is page-controlled via `useProToolbarSticky`:
           *   - sticky=true  → inside <IonHeader>, pinned to the top.
           *   - sticky=false → inside <IonContent>, scrolls with the page
           *     (default behavior; matches the comment-out below).
           * The toolbar JSX is identical in both cases; only its parent
           * changes. The status-bar safe-area strip above stays on
           * IonContent's white background regardless.
           */}
          {sticky && <IonHeader>{toolbar}</IonHeader>}
          <IonContent
            ref={contentRef}
            style={{
              '--background': 'var(--ion-background-color)',
              // Sticky pages pin the toolbar in IonHeader (which already clears the
              // status bar), so adding the top inset here too would double-count it
              // and leave a gap above the first section. Only pad when the toolbar
              // scrolls inside the content.
              '--padding-top': sticky
                ? '0px'
                : 'var(--ion-safe-area-top, env(safe-area-inset-top, 0px))',
              '--padding-bottom': `var(--pro-tabbar-reserve, ${tabBarHeight + 16}px)`,
            } as React.CSSProperties}
          >
            {!sticky && toolbar}

            <Outlet />
          </IonContent>
        </IonPage>

        {/* Drawer Overlay */}
        {drawerOpen && (
          <div
            className="fixed inset-0 z-[10000]"
            onClick={() => setDrawerOpen(false)}
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/50" />

            {/* Drawer Panel */}
            <div
              className="absolute top-0 left-0 bottom-0 w-72 bg-white dark:bg-background-dark-primary shadow-xl flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drawer Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-border-light dark:border-border-dark"
                style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <img
                    src={logomark}
                    alt="Capitol Commons"
                    className="h-9 w-auto flex-shrink-0"
                  />
                  <span className="text-base font-bold text-text-primary dark:text-text-dark-primary truncate">
                    {t('common.capitolCommons')}
                  </span>
                </div>
                <button
                  onClick={() => setDrawerOpen(false)}
                  className="p-1"
                  aria-label={t('a11y.closeMenu')}
                >
                  <IonIcon icon={closeOutline} className="text-xl text-text-tertiary dark:text-text-dark-tertiary" />
                </button>
              </div>

              {/* Drawer Items */}
              <nav className="flex-1 overflow-y-auto py-2">
                {DRAWER_ITEMS.map((item) => {
                  const Icon = item.icon;
                  const isActive = !item.placeholder && (
                    location.pathname === item.href ||
                    (item.href === '/pro/home' && location.pathname === '/pro/home')
                  );
                  return (
                    <button
                      key={item.nameKey}
                      onClick={() => {
                        if (!item.placeholder) {
                          guardedShellNavigate(item.href);
                        }
                        setDrawerOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                        isActive
                          ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                          : 'text-text-primary dark:text-text-dark-primary hover:bg-background-tertiary dark:hover:bg-background-dark-secondary'
                      } ${item.placeholder ? 'opacity-50' : ''}`}
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" />
                      <span className="text-sm font-medium">{t(item.nameKey)}</span>
                      {item.placeholder && (
                        <span className="ml-auto text-[10px] font-medium text-text-tertiary dark:text-text-dark-tertiary bg-background-tertiary dark:bg-background-dark-secondary px-1.5 py-0.5 rounded">
                          {t('common.soon')}
                        </span>
                      )}
                    </button>
                  );
                })}
              </nav>

              <div
                className="border-t border-border-light dark:border-border-dark py-2"
                style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)' }}
              >
                <button
                  type="button"
                  onClick={() => {
                    guardedShellNavigate('/pro/profile');
                    setDrawerOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                    location.pathname.startsWith('/pro/profile')
                      ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                      : 'text-text-primary dark:text-text-dark-primary hover:bg-background-tertiary dark:hover:bg-background-dark-secondary'
                  }`}
                >
                  <UserCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm font-medium">{t('nav.profile')}</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDrawerOpen(false);
                    setShowSignOutConfirm(true);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <LogOut className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm font-medium">{t('nav.signOut')}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        <ProPageModal
          isOpen={showSignOutConfirm}
          onDismiss={() => setShowSignOutConfirm(false)}
          title={t('common.signOutConfirmTitle')}
        >
          <div className="px-4 py-5 space-y-4">
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary leading-relaxed">
              {t('common.signOutConfirmBody')}
            </p>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => void proSignOut()}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-red-600 text-white font-semibold text-sm transition-colors active:bg-red-700"
              >
                <IonIcon icon={logOutOutline} className="text-lg" />
                {t('nav.signOut')}
              </button>
              <button
                type="button"
                onClick={() => setShowSignOutConfirm(false)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-transparent text-text-secondary dark:text-text-dark-secondary font-semibold text-sm transition-colors active:bg-black/5 dark:active:bg-white/5"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </ProPageModal>

        {/* Bottom Tab Bar */}
        <div
          className="pro-tab-bar absolute bottom-0 left-0 right-0 z-[9999] bg-white dark:bg-background-dark-secondary border-t border-border-light dark:border-border-dark flex items-center justify-around pt-1"
          style={{
            height: `calc(${tabBarHeight}px + var(--cc-safe-area-bottom))`,
            paddingBottom: 'var(--cc-safe-area-bottom)',
          }}
        >
          {tabs.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => guardedShellNavigate(tab.path)}
                className={`flex flex-col items-center justify-center flex-1 h-full relative transition-colors ${
                  isActive
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-text-tertiary dark:text-text-dark-tertiary'
                }`}
              >
                <IonIcon
                  icon={isActive ? tab.activeIcon : tab.inactiveIcon}
                  className="text-xl mb-0.5"
                />
                <span className="text-[10px] font-medium">{tab.label}</span>
                {tab.key === 'notifications' && unreadCount > 0 && (
                  <span className="absolute top-1 right-1/4 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/**
 * Renders the page-supplied right-slot content into the toolbar.
 * Must live inside ProToolbarSlotProvider (now wrapped at the App router
 * level — see App.tsx). Returns null when no page set a slot.
 */
const ToolbarEndSlot: React.FC = () => {
  const endSlot = useProToolbarEndSlot();
  if (!endSlot) return null;
  return <IonButtons slot="end">{endSlot}</IonButtons>;
};

export default ProTabLayout;
